import { LicencedIntervenor } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicencedIntervenor";
import { MDClinician } from "@/Common/Api/MedfarDictionaries/Dto/Responses/MDClinician";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { DoctorSearchAutocomplete } from "@/Common/Components/Search/DoctorSearchAutocomplete";
import { LabSearchAutocomplete } from "@/Common/Components/Search/LabSearchAutocomplete";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { PatientRefIntervenorWithDetailsModel } from "@/Common/Models/Patient/PatientRefIntervenorWithDetailsModel";
import { IPatientCliniciansModel } from "@/Common/Models/Patient/UI/Profile/IPatientCliniciansModel";

interface IFaxAndSignatureClinicianSelectionProps {
    SelectedClinician?: MDClinician;
    Model?: IPatientCliniciansModel;
    useLabsSearch?: boolean;
    useNameAndFaxOnly?: boolean;
    StateUpdateDelegate?: (newState: FaxAndSignatureClinicianSelectionState) => void;
    FaxValidStatusChangeDelegate?: (isValid: boolean) => void;
}

class FaxAndSignatureClinicianSelectionState {
    public CliniciansOnFile: PatientRefIntervenorWithDetailsModel[];
    public SelectedClinician: PatientRefIntervenorWithDetailsModel;

    public CustomClinicianId: string = "";
    public CustomClinicianIdentifier: string = "";
    public CustomClinicianName: string = "";
    public CustomClinicianPermitNumber: string = "";
    public CustomClinicianSpeciality: string = "";
    public CustomClinicianAddress: string = "";
    public CustomClinicianPhoneNumber: string = "";
    public CustomClinicianFaxNumber: string = "";

    public IsSending: boolean = false;
    public IsFaxValid: boolean = false;
    public IsPhoneValid: boolean = false;
    public IsEditingCustomClinician: boolean = false;
    public IsOtherSelected: boolean = false;

    constructor(s: FaxAndSignatureClinicianSelectionState = null) {
        if (s != null) {
            this.CliniciansOnFile = s.CliniciansOnFile;
            this.SelectedClinician = s.SelectedClinician;

            this.CustomClinicianId = s.CustomClinicianId;
            this.CustomClinicianIdentifier = s.CustomClinicianIdentifier;
            this.CustomClinicianName = s.CustomClinicianName;
            this.CustomClinicianPermitNumber = s.CustomClinicianPermitNumber;
            this.CustomClinicianSpeciality = s.CustomClinicianSpeciality;
            this.CustomClinicianAddress = s.CustomClinicianAddress;
            this.CustomClinicianPhoneNumber = s.CustomClinicianPhoneNumber;
            this.CustomClinicianFaxNumber = s.CustomClinicianFaxNumber;

            this.IsSending = s.IsSending;
            this.IsFaxValid = s.IsFaxValid;
            this.IsPhoneValid = s.IsPhoneValid;
            this.IsEditingCustomClinician = s.IsEditingCustomClinician;
            this.IsOtherSelected = s.IsOtherSelected;
        }
    }
}

class FaxAndSignatureClinicianSelection extends React.Component<IFaxAndSignatureClinicianSelectionProps, FaxAndSignatureClinicianSelectionState> {
    private phoneValidationRule = new ValidationHelper().getPhoneNumberValidationRule(cacheStore.SiteCache[0].Model.Adresses[0].CountryLookupId, null);
    private validationRegex: string = this.phoneValidationRule ? this.phoneValidationRule.ValidationRegexPattern : "";
    private phoneMask: string = this.phoneValidationRule ? this.phoneValidationRule.EditMaskPattern : "";

    constructor(props: IFaxAndSignatureClinicianSelectionProps) {
        super(props);

        const initialState = new FaxAndSignatureClinicianSelectionState();
        initialState.CliniciansOnFile = this.props.Model ? this.props.Model.get("IntervenorsOnFile").toArray() : null;
        initialState.SelectedClinician = null;
        initialState.CustomClinicianId = "";
        initialState.CustomClinicianIdentifier = "";
        initialState.CustomClinicianName = "";
        initialState.CustomClinicianPermitNumber = "";
        initialState.CustomClinicianSpeciality = "";
        initialState.CustomClinicianAddress = "";
        initialState.CustomClinicianPhoneNumber = "";
        initialState.CustomClinicianFaxNumber = "";
        initialState.IsSending = false;
        initialState.IsFaxValid = false;
        initialState.IsPhoneValid = false;
        initialState.IsEditingCustomClinician = false;
        initialState.IsOtherSelected = true;
        this.state = initialState;
    }

    public componentDidUpdate(prevProps: IFaxAndSignatureClinicianSelectionProps, prevState: FaxAndSignatureClinicianSelectionState): void {
        if (this.state != prevState && this.props.StateUpdateDelegate != null) {
            this.props.StateUpdateDelegate(this.state);
        }
    }

    public render(): JSX.Element {
        const nameAutocompleteField: JSX.Element = this.props.useLabsSearch ? (
            <LabSearchAutocomplete
                componentId="custom_lab"
                onItemSelectCallback={lab => {
                    const labModel = lab.Model;
                    let s = new FaxAndSignatureClinicianSelectionState(this.state);
                    s.CustomClinicianId = labModel.Id.toString();
                    s.CustomClinicianPermitNumber = "";
                    s.CustomClinicianIdentifier = "";
                    s.CustomClinicianName = labModel.Name;
                    s.CustomClinicianPhoneNumber = lab.GetFormattedPhone();
                    s.CustomClinicianSpeciality = "";
                    s.CustomClinicianAddress = lab.getAddress();
                    s.CustomClinicianFaxNumber = lab.Model.Fax;

                    s.IsOtherSelected = false;

                    s = this.setFaxValidity(s);
                    s = this.setPhoneValidity(s);
                    this.setState(s);
                }}
                defaultText={this.state.CustomClinicianName}
                onTextChangedFunction={this.onClinicianSearchChanged.bind(this)}
            />
        ) : (
            <DoctorSearchAutocomplete
                componentId="custom_doctor"
                searchOnMount={true}
                onItemSelectCallback={this.onClinicianInfoSelect.bind(this)}
                text={this.state.CustomClinicianName}
                textChangedFunction={this.onClinicianSearchChanged.bind(this)}
                isValid={this.state.CustomClinicianName != null && this.state.CustomClinicianName.trim().length > 0}
            />

        );

        return this.props.useNameAndFaxOnly ?
            this.renderCustomClinicianOnly(nameAutocompleteField) :
            this.renderDefault(nameAutocompleteField);
    }

    public renderCustomClinicianOnly(nameAutocompleteField: JSX.Element): JSX.Element {
        const renderCustomClinicianWithOnlyNameAndFax: JSX.Element = (
            <div className="flex-container m custom-clinician-block">
                <div className="custom-clinician-table">
                    <table>
                        <tbody>
                            <tr>
                                <td className="col1">
                                    <span className="float-right"> {cacheStore.GetString("NameLabel")}</span>
                                </td>
                                <td className="col2">
                                    {nameAutocompleteField}
                                </td>
                            </tr>
                            <tr>
                                <td className="col1">
                                    <span className="float-right"> {cacheStore.GetString("FaxLabel")}</span>
                                </td>
                                <td className="col2">
                                    <TextboxMaskedControl
                                        isValid={this.state.IsFaxValid}
                                        allowEmpty={false}
                                        id="fax"
                                        value={this.state.CustomClinicianFaxNumber}
                                        onChangeFunction={this.editFax.bind(this)}
                                        item={null}
                                        mask={this.phoneMask}
                                        validationErrorMessage=""
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );

        return renderCustomClinicianWithOnlyNameAndFax;
    }

    public renderDefault(nameAutocompleteField: JSX.Element): JSX.Element {
        const clinicianIds = new Set<string>();
        const clinicians = this.state.CliniciansOnFile !== null ? this.state.CliniciansOnFile
            .filter(clinician => !clinician.IsFromClinic())
            .map(clinician => {
                // duplicate clinicians mess up the radio button list
                const id = clinician.GetDoctorId() || `no-id-${sessionStore.newId()}`;
                if (clinicianIds.has(id))
                    return null;
                clinicianIds.add(id);

                // render row
                return (
                    <tr className="clinician-row" key={id} style={{ height: "50px" }}>
                        <td className="c1">
                            <input
                                type="radio"
                                name="clinician-option"
                                checked={this.state.SelectedClinician?.GetDoctorId() == id}
                                disabled={this.state.IsSending}
                                onChange={() => this.setSelectedClinician(clinician, false)}
                            />
                        </td>
                        <td className="c2" onClick={() => this.setSelectedClinician(clinician, false)}>
                            <div className="float-left"><b>{clinician.GetName()}</b></div>
                            <div className="float-left"><span className="vertical-separator"> | </span><b>{cacheStore.GetString("FaxDLabel")}</b> <span>{clinician.GetFax()}</span></div>
                            <div className="float-left"><span className="vertical-separator"> | </span><b>{cacheStore.GetString("PhoneAbbrLabel")}</b> <span>{clinician.GetPhone()}</span></div>
                            <div style={{ clear: "left" }}><b>{cacheStore.GetString("AddressLabel")}: </b> <span>{clinician.GetAddress()}</span></div>
                        </td>
                    </tr>
                );
            })
            .filter(row => !!row) : [];

        const cliniciansTable: JSX.Element = (
            <div className="flex-container m clinician-block">
                <table className="clinicians-table">
                    <tbody>
                        {clinicians}
                        <tr className="clinician-row" key="clinician_other">
                            <td className="c1">
                                <input type="radio" name="clinician-option" checked={this.state.IsOtherSelected} disabled={this.state.IsSending} onChange={this.ClearCustomFields.bind(this)} />
                            </td>
                            <td className="c2" onClick={this.ClearCustomFields.bind(this)}>
                                <div className="float-left"><b>{cacheStore.GetString("OtherSingularLabel")}</b></div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );

        const customClinician: JSX.Element = (
            <div className="flex-container m custom-clinician-block">
                <div className="custom-clinician-table">
                    <table>
                        <tbody>
                            <tr>
                                <td className="col1">
                                    <span className="float-right"> {cacheStore.GetString("NameLabel")}</span>
                                </td>
                                <td className="col2">
                                    {nameAutocompleteField}
                                </td>
                            </tr>
                            <tr>
                                <td className="col1">
                                    <span className="float-right"> {cacheStore.GetString("PermitNumberLabel")}</span>
                                </td>
                                <td className="col2">
                                    <input type="text" className="form-control" disabled={this.state.IsSending} onChange={this.editIdentifier.bind(this)} value={this.state.CustomClinicianPermitNumber} />
                                </td>
                            </tr>
                            <tr>
                                <td className="col1">
                                    <span className="float-right"> {cacheStore.GetString("SpecializationLabel")}</span>
                                </td>
                                <td className="col2">
                                    <input type="text" className="form-control" disabled={this.state.IsSending} onChange={this.editSpeciality.bind(this)} value={this.state.CustomClinicianSpeciality} />
                                </td>
                            </tr>
                            <tr>
                                <td className="col1">
                                    <span className="float-right"> {cacheStore.GetString("AddressLabel")}</span>
                                </td>
                                <td className="col2">
                                    <input type="text" className="form-control" disabled={this.state.IsSending} onChange={this.editAddress.bind(this)} value={this.state.CustomClinicianAddress} />
                                </td>
                            </tr>
                            <tr>
                                <td className="col1">
                                    <span className="float-right"> {cacheStore.GetString("PhoneNumberLabel")}</span>
                                </td>
                                <td className="col2">
                                    <TextboxMaskedControl isValid={this.state.IsPhoneValid} allowEmpty={true} id="phone" value={this.state.CustomClinicianPhoneNumber} onChangeFunction={this.editPhoneNumber.bind(this)} item={null} mask={this.phoneMask} validationErrorMessage="" />
                                </td>
                            </tr>
                            <tr>
                                <td className="col1">
                                    <span className="float-right"> {cacheStore.GetString("FaxLabel")}</span>
                                </td>
                                <td className="col2">
                                    <TextboxMaskedControl isValid={this.state.IsFaxValid} allowEmpty={false} id="fax" value={this.state.CustomClinicianFaxNumber} onChangeFunction={this.editFax.bind(this)} item={null} mask={this.phoneMask} validationErrorMessage="" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );

        return (
            <div>
                {cliniciansTable}
                {customClinician}
            </div>
        );
    }

    private setSelectedClinician(clinician: PatientRefIntervenorWithDetailsModel, IsOtherSelected: boolean = null): void {
        var s = new FaxAndSignatureClinicianSelectionState(this.state);
        s.SelectedClinician = clinician;
        s.CustomClinicianName = clinician.GetName();
        s.CustomClinicianAddress = IsNullOrWhiteSpace(clinician.GetAddress()) ? "" : clinician.GetAddress();
        s.CustomClinicianFaxNumber = IsNullOrWhiteSpace(clinician.GetFax()) ? "" : clinician.GetFax();
        s.CustomClinicianId = clinician.GetDoctorId();
        s.CustomClinicianPermitNumber = IsNullOrWhiteSpace(clinician.GetLicence()) ? "" : clinician.GetLicence();
        s.CustomClinicianPhoneNumber = IsNullOrWhiteSpace(clinician.GetPhone()) ? "" : clinician.GetPhone();
        s.CustomClinicianSpeciality = IsNullOrWhiteSpace(clinician.getSpecialtyName()) ? "" : clinician.getSpecialtyName();

        if (IsOtherSelected != null)
            s.IsOtherSelected = IsOtherSelected;

        s = this.setFaxValidity(s);
        s = this.setPhoneValidity(s);
        this.setState(s);
    }

    private setPhoneValidity(s: FaxAndSignatureClinicianSelectionState): FaxAndSignatureClinicianSelectionState {
        var regexp = new RegExp(this.validationRegex, "i");
        var valid = (s.CustomClinicianPhoneNumber == null || s.CustomClinicianPhoneNumber.trim().length == 0);
        if (!valid) {
            valid = regexp.test(s.CustomClinicianPhoneNumber);
        }
        s.IsPhoneValid = valid;

        return s;
    }

    private setFaxValidity(s: FaxAndSignatureClinicianSelectionState): FaxAndSignatureClinicianSelectionState {
        var regexp = new RegExp(this.validationRegex, "i");
        var valid = regexp.test(s.CustomClinicianFaxNumber);
        valid = valid && s.CustomClinicianFaxNumber != null && s.CustomClinicianFaxNumber.trim().length > 0;
        s.IsFaxValid = valid;

        if (this.props.FaxValidStatusChangeDelegate != null)
            this.props.FaxValidStatusChangeDelegate(s.IsFaxValid && s.CustomClinicianName != null && s.CustomClinicianName.length > 0);

        return s;
    }

    private getUnmaskedPhoneNumber(phoneNumber: string): string {
        return phoneNumber.replace(/[^0-9]/gi, "");
    }

    private onClinicianInfoSelect(item: LicencedIntervenor): void {
        if (item != null) {
            var intervenor = PatientRefIntervenorWithDetailsModel.FromLicencedintervenor(item);
            this.SelectIntervenor(intervenor);
        }
        else
            this.ClearCustomFields();
    }

    public ClearCustomFields(): void {
        var s = new FaxAndSignatureClinicianSelectionState(this.state);
        s.SelectedClinician = null;
        s.CustomClinicianName = "";
        s.CustomClinicianPermitNumber = "";
        s.CustomClinicianSpeciality = "";
        s.CustomClinicianAddress = "";
        s.CustomClinicianPhoneNumber = "";
        s.CustomClinicianFaxNumber = "";
        s.IsOtherSelected = true;
        s = this.setFaxValidity(s);
        this.setState(s);
    }

    public SelectIntervenor(intervenor: PatientRefIntervenorWithDetailsModel, IsOtherSelected: boolean = null): void {
        var intervenorLicence = intervenor.Model.LicencedIntervenor;

        var s = new FaxAndSignatureClinicianSelectionState(this.state);
        s.CustomClinicianId = intervenorLicence.Id;
        s.CustomClinicianPermitNumber = IsNullOrWhiteSpace(intervenor.GetLicence()) ? "" : intervenor.GetLicence();
        s.CustomClinicianIdentifier = intervenor.GetLicence();
        s.CustomClinicianName = intervenor.GetName();
        s.CustomClinicianPhoneNumber = IsNullOrWhiteSpace(intervenor.GetPhone()) ? "" : intervenor.GetPhone();
        s.CustomClinicianSpeciality = IsNullOrWhiteSpace(intervenorLicence.SpecialityDetails) ? "" : intervenorLicence.SpecialityDetails;
        s.CustomClinicianAddress = IsNullOrWhiteSpace(intervenor.GetAddress()) ? "" : intervenor.GetAddress();
        s.CustomClinicianFaxNumber = IsNullOrWhiteSpace(intervenor.GetFax()) ? "" : this.getUnmaskedPhoneNumber(intervenor.GetFax());

        if (IsOtherSelected != null)
            s.IsOtherSelected = IsOtherSelected;

        s = this.setFaxValidity(s);
        s = this.setPhoneValidity(s);
        this.setState(s);
    }

    private onClinicianSearchChanged(val: string): void {
        var s = new FaxAndSignatureClinicianSelectionState(this.state);
        s.CustomClinicianName = val;
        this.setState(s);

        if (this.props.FaxValidStatusChangeDelegate != null)
            this.props.FaxValidStatusChangeDelegate(s.IsFaxValid && s.CustomClinicianName != null && s.CustomClinicianName.length > 0);
    }

    private editIdentifier(e: React.ChangeEvent<HTMLInputElement>): void {
        var val = e.target.value;
        var s = new FaxAndSignatureClinicianSelectionState(this.state);
        s.CustomClinicianPermitNumber = val;

        this.setState(s);
    }

    private editSpeciality(e: React.ChangeEvent<HTMLInputElement>): void {
        var val = e.target.value;
        var s = new FaxAndSignatureClinicianSelectionState(this.state);
        s.CustomClinicianSpeciality = val;

        this.setState(s);
    }

    private editAddress(e: React.ChangeEvent<HTMLInputElement>): void {
        var val = e.target.value;
        var s = new FaxAndSignatureClinicianSelectionState(this.state);
        s.CustomClinicianAddress = val;
        this.setState(s);
    }

    private editPhoneNumber(unmasked: string, masked: string): void {
        var s = new FaxAndSignatureClinicianSelectionState(this.state);
        s.CustomClinicianPhoneNumber = unmasked;
        s = this.setPhoneValidity(s);
        if (s.IsPhoneValid) {
            s.CustomClinicianPhoneNumber = masked;
        }
        this.setState(s);
    }

    private editFax(unmasked: string, masked: string): void {
        var s = new FaxAndSignatureClinicianSelectionState(this.state);
        s.CustomClinicianFaxNumber = unmasked;
        this.setFaxValidity(s);
        if (s.IsFaxValid)
            s.CustomClinicianFaxNumber = masked;

        this.setState(s);
    }
}

export { FaxAndSignatureClinicianSelection, FaxAndSignatureClinicianSelectionState };
